import { Router } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { Account, Address, Customer, CustomerDocumentSettings, CustomerMfaSettings, CustomerNotificationSettings, CustomerPreferences, CustomerPreferredLanguageSettings, CustomerSecuritySettings } from '../../models/customer';
import { State } from '../../services/global-state';
import { Business } from '../../models/business';
import { EventAggregator } from 'aurelia-event-aggregator';
import { mock, instance, when, verify, anything, resetCalls } from 'ts-mockito';
import { AutopayConfirmation } from './edit-autopay';
import { AutopayService } from './autopay.service';
import { Autopay, AutopayFrequencyOption } from '../../models/autopay';
import { ApiResponse } from '../../models/api-response';
import { FeeCalculationValue } from '../../models/feeCalculationValue';
import { TemplateData } from '../../models/template-data';
import { Portfolio } from '../../models/portfolio';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('autopay', () => {
			describe('editautopay', () => {
				let component: AutopayConfirmation;
				const mockRouter: Router = mock(Router);
				let stubState: State;
				const mockI18N: I18N = mock(I18N);
				const mockEventAggregator: EventAggregator = mock(EventAggregator);
				const mockBusiness: Business = mock(Business);
				const mockAutoPayService: AutopayService = mock(AutopayService);
				let mockAccount: Account = mock(Account);
				const mockAutoPay: Autopay = mock(Autopay);
				const mockFeeCalculationValue: FeeCalculationValue = mock(FeeCalculationValue);
				const mockAutopayFrequencyOption: AutopayFrequencyOption = mock(AutopayFrequencyOption);
				const mockCustomerSecuritySettings: CustomerSecuritySettings = mock(CustomerSecuritySettings);
				const mockCustomerNotificationSettings: CustomerNotificationSettings = mock(CustomerNotificationSettings);
				const mockCustomerDocumentSettings: CustomerDocumentSettings = mock(CustomerDocumentSettings);
				const mockCustomerPreferredLanguageSettings: CustomerPreferredLanguageSettings = mock(CustomerPreferredLanguageSettings);
				const mockCustomerMfaSettings: CustomerMfaSettings = mock(CustomerMfaSettings);
				const mockAddress: Address = mock(Address);
				const mockCustomerPreferences: CustomerPreferences = mock(CustomerPreferences);
				let mockPortfolio: Portfolio = mock(Portfolio);

				let currentCustomer: Customer;
				let customerConfig: any = {
					securitySettings: instance(mockCustomerSecuritySettings),
					notificationSettings: instance(mockCustomerNotificationSettings),
					documentSettings: instance(mockCustomerDocumentSettings),
					preferredLanguageSettings: instance(mockCustomerPreferredLanguageSettings),
					mfaSettings: instance(mockCustomerMfaSettings),
					primaryAddress: instance(mockAddress),
					preferences: instance(mockCustomerPreferences),
					portfolios: instance(mockPortfolio)
				};

				const i18next = {
					t: (a) => a
				};


				beforeEach(() => {
					stubState = new State(
						instance(mockI18N),
						instance(mockEventAggregator)
					);

					stubState.business = instance(mockBusiness);
					stubState.business.addCustomerPortfolios = () => { };

					currentCustomer = new Customer(stubState.business, customerConfig);

					stubState.customer = currentCustomer;

					spyOn(stubState.business, 'addCustomerPortfolios').and.callFake(() => { });

					when(mockI18N.i18next).thenReturn(i18next);

					component = new AutopayConfirmation(
						stubState,
						instance(mockRouter),
						instance(mockI18N),
						instance(mockAutoPayService)
					);

					component.state = 'edit';
					component.feeValues = [];
					component.autopayService.autopay = new Autopay();

					resetCalls(mockRouter);
				});

				afterEach(() => {
					component.account = null;
					component.state = 'edit';
					component.autopayService.autopay = null
				});

				it('should initialize EditAutoPay component', () => {
					expect(component).toBeTruthy();
				});

				describe('goToReceipt()', () => {

					it('should redirect to autopay receipt', () => {

						component.account = instance(mockAccount);
						component.account.id = '999';

						when(mockRouter.navigateToRoute(anything(), anything())).thenReturn();
						component.goToReceipt();

						verify(mockRouter.navigateToRoute(anything(), anything())).called();

					});
				});

				describe('goBack()', () => {

					it('should redirect to view-autopay', () => {

						component.account = instance(mockAccount);
						component.account.id = '999';

						when(mockRouter.navigateToRoute(anything(), anything())).thenReturn();
						component.goBack();

						verify(mockRouter.navigateToRoute(anything(), anything())).called();

					});

					it('should set state as edit when current state is confirm', () => {

						component.state = 'confirm';

						component.goBack();

						expect(component.content).not.toBe(null);
						expect(component.content).toBe('');
						expect(component.state).toBe('edit');

					});
				});

				describe('submit()', () => {

					beforeEach(() => {
						component.autopay = new Autopay();
						component.autopay.key = 999;
						component.autopay.maxPaymentAmount = 1;
						component.autopay.payInFull = true;
					});

					it('should redirect to autopay receipt on success', async () => {

						const apiResponse = new ApiResponse({
							isSuccessful: true,
							value: {
								autopayKey: 123
							}
						});

						spyOn(component, 'getAutopayPaymentMethod').and.stub();
						spyOn(component, 'goToReceipt').and.stub();

						when(mockAutoPayService.updateAutopay(anything(), anything(), anything(), anything())).thenResolve(apiResponse);

						await (await component.submit());

						verify(mockAutoPayService.updateAutopay(anything(), anything(), anything(), anything())).called();
						expect(component.getAutopayPaymentMethod).toHaveBeenCalled();
						expect(component.goToReceipt).toHaveBeenCalled();

					});

					it('should handle failure', async () => {

						spyOn(component, 'getAutopayPaymentMethod').and.stub();
						when(mockAutoPayService.updateAutopay(anything(), anything(), anything(), anything())).thenReject((new Error('Some error')));
						spyOn(component, 'showError').and.stub();

						await (await component.submit());

						expect(component.getAutopayPaymentMethod).toHaveBeenCalled();
						verify(mockAutoPayService.updateAutopay(anything(), anything(), anything(), anything())).called();
						expect(component.showError).toHaveBeenCalled();

					});

				});

				describe('showConfirmation()', () => {

					it('should change state to confirm and call getConfirmationTemplateData()', () => {

						component.feeValues = [];
						component.feeValues.push(mockFeeCalculationValue);

						spyOn(component, 'validate').and.stub();
						spyOn(component, 'getConfirmationTemplateData').withArgs(component.feeValues).and.stub();

						component.showConfirmation(null);

						expect(component.getConfirmationTemplateData).toHaveBeenCalledWith(component.feeValues);
						expect(component.state).toBe('confirm');
						expect(component.paymentInformation).toBe(null);

					});

				});

				describe('validate()', () => {

					beforeEach(() => {
						component.autopay = new Autopay();
						component.autopay.payInFull = false;
						component.autopayService.autopay = instance(mockAutoPay);
					});

					it('should get no errors', () => {

						component.autopay.maxPaymentAmount = 1;
						component.autopay.payInFull = true;

						component.autopayService.autopay.maxPaymentAmount = 1;
						component.autopayService.autopay.payInFull = true;
						component.isChangingPaymentMethod = true;

						component.validate();

						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBe(0);

					});

					it('should get errors', () => {

						component.autopay.maxPaymentAmount = null;

						component.autopayService.autopay.maxPaymentAmount = null;
						component.autopayService.autopay.payInFull = true;
						component.isChangingPaymentMethod = false;

						component.validate();

						expect(component.validationErrors).not.toBe(null);
						expect(component.validationErrors.length).toBeGreaterThan(0);

					});

				});

				describe('getConfirmationTemplateData()', () => {

					it('should create a new TemplateData object', () => {

						component.feeValues = [];
						component.feeValues.push(mockFeeCalculationValue);

						stubState.business = {
							portalSettings: {
								accountNumberLabel: 'Label'
							},
							name: 'Business Name',

							getMerchantKeyAssociatedToPortfolioByPortfolioName: () => { }
						};

						component.autopay = new Autopay();
						component.autopay.maxPaymentAmount = 1;
						component.autopay.payInFull = false;
						component.autopay.frequency = instance(mockAutopayFrequencyOption);
						component.autopay.frequency.description = 'here';
						component.account = instance(mockAccount);
						component.account.referenceId = '123';
						component.account.name = 'name';
						component.paymentInformation = {};
						component.paymentInformation.firstPaymentFee = 1;
						component.paymentInformation.autopay = {
							maxPaymentAmount: 1,
							payInFull: true,
							frequency: {
								description: 'NotSet'
							}
						};

						spyOn(stubState.business, 'getMerchantKeyAssociatedToPortfolioByPortfolioName').and.stub();

						let result = component.getConfirmationTemplateData(anything());

						expect(result).not.toBe(null);
						expect(result).toBeInstanceOf(TemplateData);
						expect(result.customObject).not.toBe(null);
						expect(result.feeValues).not.toBe(null);
						expect(result.accountPayments).toBe(null);

					});

				});

				describe('activate()', () => {
					const model = { id: '123' };

					beforeEach(() => {
						stubState.customer = currentCustomer;
						stubState.customer.accounts = Array<Account>();
						component.account = {
							id: '123',
							portfolioName: 'portfolioName'
						} as Account;
						stubState.customer.accounts.push(component.account);
						stubState.customer.portfolios = [{
							portfolioName: component.account.portfolioName,
							merchantSettings: {
								feeSettings: {
									allowFeesOnAutopays: true
								}
							}
						}];

						component.autopay = new Autopay();
						component.autopay.feeValues = [];
						component.autopay.feeValues.push(instance(mockFeeCalculationValue));

					});

					it('should goback if autopay is not enabled', () => {

						component.autopayService.autopay = null;

						spyOn(component, 'goBack').and.stub();

						component.activate(model);

						expect(component.goBack).toHaveBeenCalled();

					});

					it('should set account, portfolio, autopay and feeValues ', () => {

						component.autopayService.autopay = instance(mockAutoPay);

						component.activate(model);

						expect(component.account).not.toBe(null);
						expect(component.portfolio).not.toBe(null);
						expect(component.autopay).not.toBe(null);
						expect(component.feeValues).not.toBe(null);

					});

				});

			});
		});
	});
});